import React, { useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  FileText,
  LayoutDashboard,
  Users,
} from 'lucide-react';
import Button from 'components/Button';
import Membership from 'pages/Membership';
import type { MembershipImperativeHandle } from 'pages/Membership';
import {
  DEPARTMENT_MINISTRY_REPORT_TAB_KEYS,
  departmentMinistryLabelForTabKey,
  isDepartmentMinistryReportTabKey,
  type DepartmentMinistryReportTabKey,
} from 'types/membership';
import { ROUTES } from 'utils/constants';
import styles from './ChurchReports.module.scss';

type ChurchReportPageKey = 'members' | 'departmental';

function ChurchReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const membershipReportRef = useRef<MembershipImperativeHandle>(null);
  const handleBack = () => {
    navigate(ROUTES.CHURCH.REPORTS.ROOT, { replace: true });
  };

  const pathSegments = useMemo(() => {
    const root = ROUTES.CHURCH.REPORTS.ROOT;
    const pathSuffix = location.pathname.startsWith(root)
      ? location.pathname.slice(root.length)
      : '';
    return pathSuffix.split('/').filter(Boolean);
  }, [location.pathname]);

  const activePage = useMemo(() => {
    const firstSegment = pathSegments[0];
    if (firstSegment === 'members') return 'members';
    if (firstSegment === 'departmental') return 'departmental';
    return null;
  }, [pathSegments]);

  const setPage = (page: ChurchReportPageKey | null) => {
    if (!page) {
      navigate(ROUTES.CHURCH.REPORTS.ROOT, { replace: true });
      return;
    }

    if (page === 'departmental') {
      navigate(
        ROUTES.CHURCH.REPORTS.DEPARTMENTAL_DETAIL.replace(
          ':dept',
          DEPARTMENT_MINISTRY_REPORT_TAB_KEYS[0],
        ),
        { replace: true },
      );
      return;
    }

    if (page === 'members') {
      navigate(ROUTES.CHURCH.REPORTS.MEMBERS, { replace: true });
      return;
    }
  };

  const setDepartmentalTab = (key: DepartmentMinistryReportTabKey) => {
    navigate(ROUTES.CHURCH.REPORTS.DEPARTMENTAL_DETAIL.replace(':dept', key), {
      replace: true,
    });
  };

  const departmentalCategory = useMemo((): DepartmentMinistryReportTabKey => {
    if (activePage !== 'departmental') {
      return DEPARTMENT_MINISTRY_REPORT_TAB_KEYS[0];
    }
    const deptSegment = pathSegments[1];
    if (isDepartmentMinistryReportTabKey(deptSegment)) return deptSegment;
    return DEPARTMENT_MINISTRY_REPORT_TAB_KEYS[0];
  }, [activePage, pathSegments]);

  return (
    <div className={styles.container}>
      <header
        className={`${styles.header} ${
          activePage === 'departmental' ? styles.headerDepartmental : ''
        }`}
      >
        <div
          className={`${styles.headerLeft} ${
            activePage === 'departmental' ? styles.headerLeftStacked : ''
          }`}
        >
          {activePage ? (
            <Button variant="ghost" onClick={handleBack} aria-label="Go back">
              <ArrowLeft size={16} />
              Back
            </Button>
          ) : (
            <h1 className={styles.pageTitle}>Church</h1>
          )}
          {activePage === 'departmental' && (
            <h1 className={styles.pageSubTitle}>Departmental Reports</h1>
          )}
        </div>
        {(activePage === 'members' || activePage === 'departmental') && (
          <div className={styles.reportToolbar}>
            <Button
              variant="outline"
              onClick={() => void membershipReportRef.current?.downloadPdf()}
            >
              <FileText size={16} />
              Download PDF
            </Button>
          </div>
        )}
      </header>

      {!activePage ? (
        <div className={styles.cardGrid} role="navigation">
          <button
            type="button"
            className={styles.card}
            onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
          >
            <div className={styles.cardIcon} aria-hidden="true">
              <Users size={24} />
            </div>
            <div className={styles.cardText}>
              <div className={styles.cardTitle}>General Member&apos;s List</div>
              <div className={styles.cardSubtitle}>
                View and manage all registered church members
              </div>
            </div>
          </button>

          <button
            type="button"
            className={styles.card}
            onClick={() => navigate(ROUTES.MEMBERSHIP.WEBSITE_BUILDER)}
          >
            <div className={styles.cardIcon} aria-hidden="true">
              <LayoutDashboard size={24} />
            </div>
            <div className={styles.cardText}>
              <div className={styles.cardTitle}>Website Builder</div>
              <div className={styles.cardSubtitle}>
                Build and publish pages for your church website
              </div>
            </div>
          </button>

          <button
            type="button"
            className={styles.card}
            onClick={() => setPage('members')}
          >
            <div className={styles.cardIcon} aria-hidden="true">
              <FileText size={24} />
            </div>
            <div className={styles.cardText}>
              <div className={styles.cardTitle}>Member&apos;s Lists Report</div>
              <div className={styles.cardSubtitle}>
                Pastors, Board/Council, Leaders, Members, Attenders, and NEF
              </div>
            </div>
          </button>

          <button
            type="button"
            className={styles.card}
            onClick={() => setPage('departmental')}
          >
            <div className={styles.cardIcon} aria-hidden="true">
              <Building2 size={24} />
            </div>
            <div className={styles.cardText}>
              <div className={styles.cardTitle}>Departmental Report</div>
              <div className={styles.cardSubtitle}>
                Ministry teams and other church departments
              </div>
            </div>
          </button>
        </div>
      ) : (
        <div
          className={`${styles.pageBody} ${
            activePage === 'departmental' ? styles.pageBodyDepartmental : ''
          }`}
        >
          {activePage === 'members' && (
            <Membership
              ref={membershipReportRef}
              showAddMembershipButton={false}
              showActionButtons={false}
              showUtilityButtons={false}
              alignContainerStart
              showTitle
              smallTitle
              titleText="Member's Lists Report"
            />
          )}

          {activePage === 'departmental' && (
            <>
              <div
                className={`${styles.filters} ${styles.filtersDepartmental}`}
              >
                <div className={styles.tabsRow}>
                  {DEPARTMENT_MINISTRY_REPORT_TAB_KEYS.map((tabKey) => (
                    <Button
                      key={tabKey}
                      size="sm"
                      variant={
                        departmentalCategory === tabKey ? 'primary' : 'outline'
                      }
                      onClick={() => setDepartmentalTab(tabKey)}
                    >
                      {departmentMinistryLabelForTabKey(tabKey)}
                    </Button>
                  ))}
                </div>
              </div>
              <div className={styles.departmentalMembershipTight}>
                <Membership
                  ref={membershipReportRef}
                  key={departmentalCategory}
                  showAddMembershipButton={false}
                  showActionButtons={false}
                  showUtilityButtons={false}
                  alignContainerStart
                  showTitle
                  tinyTitle
                  titleText={departmentMinistryLabelForTabKey(
                    departmentalCategory,
                  )}
                  departmentMinistryFilter={departmentMinistryLabelForTabKey(
                    departmentalCategory,
                  )}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ChurchReports;
